/**
 * Transaction DTO exposed by payment-service `/api/transactions`.
 * Mirrors `net.nawam.payment.model.entity.Transaction`.
 *
 * `metadata` is a free-form JSON string with a `type` discriminator —
 * see {@link TransactionMetadata}. Cross-service convention documented
 * in `CLAUDE.md` (Transaction Metadata Convention).
 */

import type { UserRef } from './user.types';

export type TransactionStatus =
  | 'INITIATED'
  | 'PENDING'
  | 'SUCCESSFUL'
  | 'FAILED'
  | 'EXPIRED';

/** Source service identifier (matches `Transaction.sourceService` values). */
export type SourceService =
  | 'rental'
  | 'wallet'
  | 'operator'
  | 'auth'
  | string;

export interface PaymentMethodRef {
  id?: number;
  name?: string | null;
  type?: string | null;
  /** Vendor code shown to GUTouch, e.g. `WAVE_SN_API_CASH_IN`. */
  serviceCode?: string | null;
  logoUrl?: string | null;
}

export interface Transaction {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── identity ──────────────────────────────────────────────────────────
  idFromClient: string;
  idFromGU?: string | null;
  numTransaction?: string | null;

  // ── money ─────────────────────────────────────────────────────────────
  amount: number;
  fees?: number | null;
  currency: string;

  // ── recipient ─────────────────────────────────────────────────────────
  recipientNumber?: string | null;
  recipientEmail?: string | null;
  recipientFirstName?: string | null;
  recipientLastName?: string | null;

  // ── lifecycle ─────────────────────────────────────────────────────────
  serviceCode: string;
  transactionStatus: TransactionStatus;
  paymentUrl?: string | null;
  qrCode?: string | null;
  deepLink?: string | null;
  maxit?: string | null;
  om?: string | null;
  callbackReceived: boolean;
  callbackReceivedAt?: string | null;

  // ── cross-service link ────────────────────────────────────────────────
  sourceService: SourceService;
  sourceEntityId: string;
  sourceEntityType?: string | null;

  // ── redirects ─────────────────────────────────────────────────────────
  returnUrl?: string | null;
  cancelUrl?: string | null;

  // ── parties ───────────────────────────────────────────────────────────
  userId?: number | null;
  /** Read-model du user qui a initié la transaction. */
  user?: UserRef | null;
  paymentMethod?: PaymentMethodRef | null;

  /** Free-form JSON string. Parse via {@link parseTransactionMetadata}. */
  metadata?: string | null;
}

/**
 * Transaction de paiement d'une **facture**, relayée par rental-service :
 * `GET /rental/api/invoices/{id}/transactions` (§2.8.bis) — tableau brut.
 *
 * ⚠️ **Ne pas retenter `GET /payment/api/transactions/by-source` côté owner** :
 * réservé `ROLE_ADMIN`/`transaction_list` → 403, et le restera (payment-service
 * ne sait pas à qui appartient une facture). Ici l'ownership est vérifié côté
 * rental (locataire OU loueur de la facture), qui relaie en S2S.
 *
 * Sous-ensemble de {@link Transaction} (pas de `user`/`paymentMethod`/URLs de
 * paiement) ; `metadata` reste la JSON string à parser via
 * {@link parseTransactionMetadata} — l'affichage switche sur `metadata.type`.
 */
export interface PaymentTransactionView {
  id: number;
  numTransaction?: string | null;
  idFromGU?: string | null;
  amount: number;
  fees?: number | null;
  currency: string;
  serviceCode?: string | null;
  transactionStatus: TransactionStatus;
  callbackReceived?: boolean;
  callbackReceivedAt?: string | null;
  sourceEntityType?: string | null;
  clientChannel?: string | null;
  /** Le **payeur** (= le client/locataire, pas l'owner) — pas de deeplink owner. */
  userId?: number | null;
  metadata?: string | null;
  createdAt?: string | null;
}

// ── Metadata discriminator (CLAUDE.md convention) ──────────────────────

export type TransactionMetadataType =
  | 'RENTAL'
  | 'RIDE'
  | 'UV_PURCHASE'
  | 'WITHDRAWAL'
  | 'WALLET_TOPUP'
  | 'WALLET_WITHDRAWAL';

/** Union of all known metadata shapes (others fall back to `BaseMetadata`). */
export interface BaseMetadata {
  type: TransactionMetadataType | string;
  [key: string]: unknown;
}
export interface RentalMetadata extends BaseMetadata {
  type: 'RENTAL';
  vehicle?: string;
  immatriculation?: string;
  startDate?: string;
  endDate?: string;
  invoiceNumber?: string;
}
export interface RideMetadata extends BaseMetadata {
  type: 'RIDE';
  vehicle?: string;
  immatriculation?: string;
  startLocation?: string;
  endLocation?: string;
}
export interface UvPurchaseMetadata extends BaseMetadata {
  type: 'UV_PURCHASE';
  uvAmount?: number;
}
export interface WithdrawalMetadata extends BaseMetadata {
  type: 'WITHDRAWAL';
  source?: string;
}
export interface WalletTopupMetadata extends BaseMetadata {
  type: 'WALLET_TOPUP';
  ownerType?: 'OWNER' | 'DRIVER' | 'OPERATOR' | string;
  ownerRef?: number;
}
export interface WalletWithdrawalMetadata extends BaseMetadata {
  type: 'WALLET_WITHDRAWAL';
  ownerType?: 'OWNER' | 'DRIVER' | 'OPERATOR' | string;
  ownerRef?: number;
}

export type TransactionMetadata =
  | RentalMetadata
  | RideMetadata
  | UvPurchaseMetadata
  | WithdrawalMetadata
  | WalletTopupMetadata
  | WalletWithdrawalMetadata
  | BaseMetadata;

/** Safe JSON parse — returns `null` on failure or absent. */
export function parseTransactionMetadata(raw: string | null | undefined): TransactionMetadata | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as TransactionMetadata;
    return null;
  } catch {
    return null;
  }
}

// ── UI helpers ─────────────────────────────────────────────────────────

import { defensiveStatusMeta } from '../helpers/status-meta';

/** Defensive : unknown/null statuses fall back to `{label:'—', palette:'gray'}`. */
export const TRANSACTION_STATUS_META = defensiveStatusMeta<TransactionStatus>({
  INITIATED: { label: 'status.transaction.INITIATED', palette: 'gray' },
  PENDING: { label: 'status.transaction.PENDING', palette: 'warning' },
  SUCCESSFUL: { label: 'status.transaction.SUCCESSFUL', palette: 'brand' },
  FAILED: { label: 'status.transaction.FAILED', palette: 'error' },
  EXPIRED: { label: 'status.transaction.EXPIRED', palette: 'gray' },
});

export const METADATA_TYPE_LABEL: Record<string, string> = {
  RENTAL: 'metadataType.RENTAL',
  RIDE: 'metadataType.RIDE',
  UV_PURCHASE: 'metadataType.UV_PURCHASE',
  WITHDRAWAL: 'metadataType.WITHDRAWAL',
  WALLET_TOPUP: 'metadataType.WALLET_TOPUP',
  WALLET_WITHDRAWAL: 'metadataType.WALLET_WITHDRAWAL',
};

export const SOURCE_SERVICE_LABEL: Record<string, string> = {
  rental: 'Rental service',
  wallet: 'Wallet service',
  operator: 'Operator service',
  auth: 'Auth service',
};
